import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Chip,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ProductSearchOption from '../components/ProductSearchOption';
import { useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import {
  useCreateSalesReturnMutation,
  useGetSalesReturnQuery,
  useDeleteSalesReturnMutation,
  useLazyGetCustomerProductHistoryQuery,
  useLazyGetCustomersQuery,
  useLazyGetSalesOrdersQuery,
  useLazyGetSalesOrderQuery,
} from '../../../store/api/salesApi';

function SalesReturnForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isView = !!id;
  const [createSalesReturn] = useCreateSalesReturnMutation();
  const [deleteSalesReturn] = useDeleteSalesReturnMutation();
  const [triggerCustomerSearch, { data: customerSearchData, isFetching: searchingCustomers }] = useLazyGetCustomersQuery();
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] = useLazyGetProductsQuery();
  const [productSearch, setProductSearch] = useState('');
  const [triggerGetInvoices, { isFetching: loadingInvoices }] = useLazyGetSalesOrdersQuery();
  const [triggerGetInvoice, { isFetching: loadingInvoiceDetails }] = useLazyGetSalesOrderQuery();
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const { data: existingReturn } = useGetSalesReturnQuery(id, {
    skip: !isView,
  });

  const [formData, setFormData] = useState({
    customerId: '',
    returnDate: new Date().toISOString().split('T')[0],
    notes: '',
    discountTotal: 0,
    roundOff: 0,
    salesOrderId: '',
  });

  useEffect(() => {
    if (customerSearchData) {
      const data = customerSearchData.data || (Array.isArray(customerSearchData) ? customerSearchData : []);
      setCustomerSearchOptions(prevOptions => {
        const currentId = formData.customerId;
        const currentInNew = data.find(c => c.id === currentId);
        const currentInOld = prevOptions.find(c => c.id === currentId);
        
        if (currentId && !currentInNew && currentInOld) {
          return [currentInOld, ...data];
        }
        return data;
      });
    }
  }, [customerSearchData, formData.customerId]);

  useEffect(() => {
    if (existingReturn?.customer && !customerSearchOptions.find(c => c.id === existingReturn.customer.id)) {
      setCustomerSearchOptions(prev => [existingReturn.customer, ...prev]);
    }
  }, [existingReturn, customerSearchOptions]);

  useEffect(() => {
    if (!isView) {
      setSelectedInvoice(null);
      setItems([]);
      setFormData(prev => ({ ...prev, salesOrderId: '' }));

      if (formData.customerId) {
        triggerGetInvoices({ customerId: formData.customerId, limit: 100, status: 'delivered' }).then((res) => {
          if (res.data?.salesOrders) {
            setCustomerInvoices(res.data.salesOrders);
          }
        });
      } else {
        setCustomerInvoices([]);
      }
    }
  }, [formData.customerId, isView, triggerGetInvoices]);

  const handleSelectInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setFormData(prev => ({ ...prev, salesOrderId: invoice ? invoice.id : '' }));
    
    if (invoice) {
      const res = await triggerGetInvoice(invoice.id);
      if (res.data && res.data.items) {
        const invoiceItems = res.data.items
          .filter(it => !it.isReturn)
          .map(it => ({
            productId: it.productId,
            productName: it.product?.name || 'Unknown Product',
            productSku: it.product?.sku || '',
            quantity: it.quantity, // Populate with invoiced quantity
            unitPrice: it.unitPrice,
            isDamaged: false,
            damageReason: '',
            originalSale: { ...it, invoiceNumber: res.data.invoiceNumber, invoiceDate: res.data.invoiceDate },
          }));
        setItems(invoiceItems);
      }
    } else {
      setItems([]);
    }
  };

  const selectedCustomer = customerSearchOptions.find((c) => c.id === formData.customerId) || null;

  const [items, setItems] = useState([]);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const [triggerHistory, { data: historyData, isFetching: loadingHistory }] = useLazyGetCustomerProductHistoryQuery();

  const handleAddItem = () => {
    setItems([...items, { productId: '', productName: '', productSku: '', quantity: 1, unitPrice: 0 }]);
  };

  const handleAddProductRow = async (product) => {
    if (!product) return;
    if (!formData.customerId) {
      alert('Please select a customer first');
      return;
    }

    const { data: history } = await triggerHistory({
      customerId: formData.customerId,
      productId: product.id,
    });

    if (!history || history.length === 0) {
      const proceed = window.confirm(
        `Warning: This customer has no purchase history for "${product.name}". Do you want to add it to the return anyway?`
      );
      if (!proceed) return;
    }

    let targetIndex = items.findIndex((it) => String(it.productId) === String(product.id));
    if (targetIndex === -1) targetIndex = items.length;

    setItems((prev) => {
      const existingIndex = prev.findIndex((it) => String(it.productId) === String(product.id));
      if (existingIndex !== -1) {
        const newItems = [...prev];
        newItems[existingIndex].quantity = (parseFloat(newItems[existingIndex].quantity) || 0) + 1;
        return newItems;
      }
      return [
        ...prev,
        {
          productId: product.id,
          productName: product.name,
          productSku: product.sku,
          quantity: 1,
          unitPrice: (history && history.length > 0) ? history[0].unitPrice : (product.sellingPrice || 0),
          originalSale: (history && history.length === 1) ? history[0] : null,
        },
      ];
    });

    if (history && history.length > 1) {
      // If multiple sales found, open the picker for the item
      setActiveItemIndex(targetIndex); 
      setHistoryDialog(true);
    }
  };

  const handleShowHistory = async (index) => {
    const item = items[index];
    if (!item.productId || !formData.customerId) return;
    
    await triggerHistory({
      customerId: formData.customerId,
      productId: item.productId,
    });
    setActiveItemIndex(index);
    setHistoryDialog(true);
  };

  const handleSelectHistoryRecord = (record) => {
    if (activeItemIndex === null) return;
    const newItems = [...items];
    newItems[activeItemIndex].unitPrice = record.unitPrice;
    newItems[activeItemIndex].originalSale = record;
    setItems(newItems);
    setHistoryDialog(false);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const vatRate = toNum(company?.vatRate || 0) / 100;
  const vatIncluded = company?.salesVatIncluded !== false;

  const subtotal = items.reduce(
    (sum, item) => sum + parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0),
    0
  );
  const discountTotal = Math.max(0, Math.min(toNum(formData.discountTotal), subtotal));
  const taxable = Math.max(0, subtotal - discountTotal);
  const vatTotal =
    vatRate > 0
      ? vatIncluded
        ? taxable - taxable / (1 + vatRate)
        : taxable * vatRate
      : 0;
  const grandTotal = vatRate > 0 ? (vatIncluded ? taxable : taxable + vatTotal) : taxable;
  const roundOff = toNum(formData.roundOff);
  const totalAmount = grandTotal + roundOff;

  useEffect(() => {
    if (existingReturn && isView) {
      setFormData((prev) => ({
        ...prev,
        customerId: existingReturn.customerId || '',
        returnDate: existingReturn.returnDate
          ? existingReturn.returnDate.substring(0, 10)
          : prev.returnDate,
        notes: existingReturn.notes || '',
        discountTotal: existingReturn.discountTotal ?? 0,
        roundOff: existingReturn.roundOff ?? 0,
        salesOrderId: existingReturn.salesOrderId || '',
      }));
      setItems(
        (existingReturn.items || []).map((it) => ({
          productId: it.productId,
          productName: it.product?.name,
          productSku: it.product?.sku,
          quantity: it.quantity,
          unitPrice: it.unitPrice,
          isDamaged: it.isDamaged || false,
          damageReason: it.damageReason || '',
        }))
      );
    }
  }, [existingReturn, isView]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) {
      navigate('/sales/returns');
      return;
    }
    if (!formData.customerId) {
      alert('Customer is required');
      return;
    }
    if (!formData.returnDate) {
      alert('Return Date is required');
      return;
    }
    if (!items.length) {
      alert('Please add at least one item');
      return;
    }
    if (items.some((i) => !i.productId)) {
      alert('Please select a product for all items');
      return;
    }

    // Validation: Return quantity vs Original Sold Quantity
    for (const item of items) {
      if (item.originalSale && parseFloat(item.quantity) > parseFloat(item.originalSale.quantity)) {
        const proceed = window.confirm(
          `Warning: Return quantity for "${item.productName}" exceeds the quantity sold in the original invoice (${item.originalSale.quantity}). Do you want to proceed?`
        );
        if (!proceed) return;
      }
    }

    try {
      await createSalesReturn({
        ...formData,
        discountTotal: toNum(formData.discountTotal),
        roundOff: toNum(formData.roundOff),
        items: items.map((item) => ({
          productId: item.productId,
          quantity: parseFloat(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          isDamaged: item.isDamaged || false,
          damageReason: item.damageReason || '',
        })),
      }).unwrap();
      navigate('/sales/returns');
    } catch (error) {
      alert(error?.data?.message || 'Error creating sales return');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (window.confirm('Are you sure you want to delete this sales return? This will also reverse the stock changes.')) {
      try {
        await deleteSalesReturn(id).unwrap();
        navigate('/sales/returns');
      } catch (err) {
        alert('Failed to delete sales return: ' + (err.data?.message || err.message));
      }
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        {isView ? 'Sales Return Details' : 'New Sales Return'}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                fullWidth
                options={customerSearchOptions}
                loading={searchingCustomers}
                getOptionLabel={(opt) => opt?.name || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                value={selectedCustomer}
                onChange={(_, newValue) => {
                  setFormData({ ...formData, customerId: newValue?.id || '' });
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === 'input') {
                    if (newInputValue && newInputValue.length >= 1) {
                      triggerCustomerSearch({ search: newInputValue, limit: 20 });
                    } else if (!newInputValue) {
                      triggerCustomerSearch({ limit: 20 });
                    }
                  }
                }}
                onOpen={() => {
                  if (customerSearchOptions.length === 0) triggerCustomerSearch({ limit: 20 });
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Customer Name *" 
                    required={!formData.customerId}
                    placeholder="Search by name"
                  />
                )}
              />
            </Grid>
            {!isView && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  fullWidth
                  options={customerInvoices}
                  loading={loadingInvoices || loadingInvoiceDetails}
                  getOptionLabel={(opt) => `${opt.invoiceNumber || opt.orderNumber} (${new Date(opt.invoiceDate).toLocaleDateString()}) - ${currency} ${Number(opt.totalAmount || 0).toFixed(company?.currencyDecimals ?? 2)}`}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  value={selectedInvoice}
                  onChange={(_, newValue) => handleSelectInvoice(newValue)}
                  disabled={!formData.customerId}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Select Invoice to Return (Optional)" 
                      placeholder={formData.customerId ? "Select an invoice..." : "Select a customer first"}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Return Date"
                name="returnDate"
                value={formData.returnDate}
                onChange={(e) => setFormData({ ...formData, returnDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#1E293B' }}>
                    Return Items
                  </Typography>
                  <Chip
                    label={`${items.length} ${items.length === 1 ? 'item' : 'items'}`}
                    size="small"
                    sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: '#EEF2F6', color: '#334155' }}
                  />
                </Box>
                {!isView && (
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: { xs: '100%', sm: 400 }, maxWidth: 640 }}>
                    <Autocomplete
                      fullWidth
                      size="medium"
                      autoHighlight
                      freeSolo
                      options={searchingProducts ? [] : (productSearchData?.products || [])}
                      loading={searchingProducts}
                      value={null}
                      inputValue={productSearch}
                      filterOptions={(x) => x}
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        if (typeof option === 'string') return option;
                        return option.name ? `${option.name} ${option.brand ? `[${option.brand}]` : ''} (${option.sku || option.barcode || ''})` : '';
                      }}
                      onFocus={() => {
                        if (!productSearchData?.products || productSearchData.products.length === 0) {
                          triggerProductSearch({ limit: 20 });
                        }
                      }}
                      onInputChange={(_, value) => {
                        setProductSearch(value);
                        if (value && value.trim().length >= 1) {
                          triggerProductSearch({ search: value.trim(), limit: 20 });
                        }
                      }}
                      onChange={(_, value) => {
                        if (value && typeof value === 'object') {
                          handleAddProductRow(value);
                          setProductSearch('');
                        }
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            borderRadius: 2.5,
                            boxShadow: '0 16px 36px -4px rgba(0, 0, 0, 0.18)',
                            border: '1px solid #CBD5E1',
                            mt: 1,
                            overflow: 'hidden',
                            '& .MuiAutocomplete-listbox': { p: 0.5, maxHeight: 380 },
                          },
                        },
                      }}
                      renderOption={(props, option) => (
                        <ProductSearchOption
                          props={props}
                          option={option}
                          currency={currency}
                          decimals={company?.currencyDecimals ?? 2}
                          isSelected={items.some((it) => String(it.productId) === String(option.id))}
                          priceMode="selling"
                        />
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Scan or search item (name / SKU / barcode)..."
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 1, bgcolor: '#F1F5F9', color: '#475569', mr: 0.5 }}>
                                  <SearchIcon sx={{ fontSize: 18 }} />
                                </Box>
                                {params.InputProps.startAdornment}
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                {searchingProducts ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} /> : null}
                                {productSearch ? (
                                  <IconButton size="small" onClick={() => setProductSearch('')} sx={{ mr: 0.5, p: 0.5 }}>
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                ) : null}
                                {params.InputProps.endAdornment}
                              </InputAdornment>
                            ),
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const list = searchingProducts ? [] : (productSearchData?.products || []);
                              if (list.length > 0) {
                                const typed = String(productSearch).trim();
                                const firstItem = list[0];
                                if (typed) {
                                  const isExactMatch =
                                    String(firstItem.barcode || '').trim() === typed ||
                                    String(firstItem.sku || '').trim() === typed ||
                                    firstItem.name.toLowerCase().includes(typed.toLowerCase());
                                  if (!isExactMatch) return;
                                }
                                handleAddProductRow(firstItem);
                                setProductSearch('');
                              }
                            }
                          }}
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 48,
                              borderRadius: 2,
                              bgcolor: '#FFFFFF',
                            },
                          }}
                        />
                      )}
                    />
                    <Button 
                      startIcon={<AddIcon />} 
                      onClick={handleAddItem} 
                      variant="outlined"
                      sx={{ whiteSpace: 'nowrap', height: 48, borderRadius: 2, fontWeight: 600 }}
                    >
                      Empty Row
                    </Button>
                  </Box>
                )}
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Unit Price (${currency})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Total (${currency})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Damaged</TableCell>
                      {!isView && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => {
                      const total = parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            {!isView && !item.productId ? (
                              <Autocomplete
                                fullWidth
                                size="small"
                                options={searchingProducts ? [] : (productSearchData?.products || [])}
                                loading={searchingProducts}
                                value={null}
                                filterOptions={(x) => x}
                                getOptionLabel={(option) =>
                                  option?.name ? `${option.name} ${option.brand ? `[${option.brand}]` : ''} (${option.sku || option.barcode || ''})` : ''
                                }
                                renderOption={(props, option) => (
                                  <ProductSearchOption
                                    props={props}
                                    option={option}
                                    currency={currency}
                                    decimals={company?.currencyDecimals ?? 2}
                                    isSelected={items.some((it) => String(it.productId) === String(option.id))}
                                    priceMode="selling"
                                  />
                                )}
                                onInputChange={(_, value) => {
                                  if (value && value.trim().length >= 1) {
                                    triggerProductSearch({ search: value.trim(), limit: 20 });
                                  }
                                }}
                                onChange={async (_, value) => {
                                  if (value && typeof value === 'object') {
                                    if (!formData.customerId) {
                                      alert('Please select a customer first');
                                      return;
                                    }
                                    const { data: history } = await triggerHistory({
                                      customerId: formData.customerId,
                                      productId: value.id,
                                    });
                                    if (!history || history.length === 0) {
                                      const proceed = window.confirm(
                                        `Warning: This customer has no purchase history for "${value.name}". Do you want to add it to the return anyway?`
                                      );
                                      if (!proceed) return;
                                    }
                                    
                                    setItems(prev => {
                                      const newItems = [...prev];
                                      newItems[index] = {
                                        ...newItems[index],
                                        productId: value.id,
                                        productName: value.name,
                                        productSku: value.sku,
                                        unitPrice: (history && history.length > 0) ? history[0].unitPrice : (value.sellingPrice || 0),
                                        originalSale: (history && history.length === 1) ? history[0] : null,
                                      };
                                      return newItems;
                                    });
                                    
                                    if (history && history.length > 1) {
                                      setActiveItemIndex(index);
                                      setHistoryDialog(true);
                                    }
                                  }
                                }}
                                renderInput={(params) => <TextField {...params} label="Search..." />}
                                sx={{ minWidth: 200 }}
                              />
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.productName || 'Unnamed Product'}
                                {item.productSku ? ` (${item.productSku})` : ''}
                                {item.originalSale && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    From Inv: {item.originalSale.invoiceNumber} ({new Date(item.originalSale.invoiceDate).toLocaleDateString()})
                                  </Typography>
                                )}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                if (item.originalSale && val > item.originalSale.quantity) {
                                  // Warning only, or strict? User asked for validation.
                                  // Let's keep it as warning for now or helper text
                                }
                                handleItemChange(index, 'quantity', e.target.value);
                              }}
                              sx={{ width: 100 }}
                              inputProps={{ min: 0.01, step: 0.01 }}
                              disabled={isView}
                              helperText={item.originalSale ? `Max: ${item.originalSale.quantity}` : ''}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                type="number"
                                size="small"
                                value={item.unitPrice}
                                onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                                sx={{ width: 120 }}
                                inputProps={{ min: 0, step: 0.01 }}
                                disabled={isView}
                              />
                              {!isView && item.productId && (
                                <IconButton size="small" onClick={() => handleShowHistory(index)} title="Sales History">
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{total.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Checkbox
                                size="small"
                                checked={!!item.isDamaged}
                                onChange={(e) => handleItemChange(index, 'isDamaged', e.target.checked)}
                                disabled={isView}
                                sx={{ p: 0 }}
                              />
                              {item.isDamaged && (
                                <TextField
                                  size="small"
                                  placeholder="Reason"
                                  value={item.damageReason || ''}
                                  onChange={(e) => handleItemChange(index, 'damageReason', e.target.value)}
                                  disabled={isView}
                                  sx={{ minWidth: 100 }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          {!isView && (
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveItem(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
                Return Summary (Auto Calculated)
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={`Subtotal (${currency})`}
                value={subtotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label={`Discount Total (${currency})`}
                type="number"
                value={formData.discountTotal}
                onChange={(e) => setFormData({ ...formData, discountTotal: e.target.value })}
                sx={{ mb: 2 }}
                inputProps={{ min: 0, step: 0.01 }}
                disabled={isView}
              />
              <TextField
                fullWidth
                label={`VAT (${currency})`}
                value={vatTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={`Grand Total (${currency})`}
                value={grandTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label={`Round Off (${currency})`}
                type="number"
                value={formData.roundOff}
                onChange={(e) => setFormData({ ...formData, roundOff: e.target.value })}
                sx={{ mb: 2 }}
                inputProps={{ step: 0.01 }}
                disabled={isView}
              />
              <TextField
                fullWidth
                label={`Total Amount (${currency})`}
                value={totalAmount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                InputProps={{ readOnly: true }}
              />
            </Grid>

              <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2 }}>
                {isView && (
                  <Button variant="outlined" color="error" onClick={handleDelete} startIcon={<DeleteIcon />}>
                    Delete Return
                  </Button>
                )}
                <Button variant="outlined" onClick={() => navigate('/sales/returns')}>
                  {isView ? 'Back to List' : 'Cancel'}
                </Button>
                {!isView && (
                  <Button variant="contained" type="submit">
                    Save Sales Return
                  </Button>
                )}
              </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Sales History Picker Dialog */}
      <Dialog open={historyDialog} onClose={() => setHistoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Select Original Sale Price
          <Typography variant="body2" color="text.secondary">
            {activeItemIndex !== null && items[activeItemIndex]?.productName}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : historyData && historyData.length > 0 ? (
            <List sx={{ pt: 0 }}>
              {historyData.map((record, i) => (
                <ListItemButton key={i} onClick={() => handleSelectHistoryRecord(record)} divider>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
                          {currency} {Number(record.unitPrice || 0).toFixed(2)}
                        </Typography>
                        <Chip 
                          label={record.status?.toUpperCase() || 'UNKNOWN'} 
                          size="small" 
                          color={['confirmed', 'shipped', 'delivered'].includes(record.status) ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" component="span" display="block">
                          {record.invoiceNumber ? `Invoice: ${record.invoiceNumber}` : `Order: ${record.orderNumber}`} | Qty: {record.quantity}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Date: {record.invoiceDate ? new Date(record.invoiceDate).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Alert severity="warning">No purchase history found for this item and customer.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SalesReturnForm;

